import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Heart, Leaf, Users, Phone, Mail, MapPin, Calendar, ArrowRight, Sparkles, Shield, BookOpen, MessageCircle, Brain, Target, Bed, Apple, Flower2, Home, Globe } from 'lucide-react';
import NavBar from '@/components/NavBar';
// Removed Footer import - App.tsx handles global footer
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import heroImage from '@/assets/hero_image.jpg';

const CycleFreeHealthPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Optimized SEO Meta Tags
    document.title = "Cycle-Free Women's Health | Currimundi Clinic";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Inclusive Chinese Medicine care for women without cycles. Located in Currimundi, Sunshine Coast. Supporting your health journey.");
    }
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Cycle-Free Women's Health | Currimundi Clinic");
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Inclusive Chinese Medicine care for women without cycles. Located in Currimundi, Sunshine Coast. Supporting your health journey.");
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/cycle-free-health");
    }
    
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/cycle-free-health-chinese-medicine.jpg");
    }
    
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Cycle-Free Women's Health | Currimundi Clinic");
    }

    // Enhanced LocalBusiness Schema
    const schema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Cycle-Free Women's Health",
      "image": "https://womenandtheway.com.au/cycle-free-health-chinese-medicine.jpg",
      "description": "Inclusive Chinese Medicine care for women without cycles. Located in Currimundi, Sunshine Coast. Supporting your health journey.",
      "url": "https://womenandtheway.com.au/cycle-free-health",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "serviceType": "Cycle-Free Health, Inclusive Care, Chinese Medicine, Acupuncture, Women's Health",
      "areaServed": "Sunshine Coast, Currimundi, Caloundra, Maroochydore",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };
    
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                  Cycle-Free Women's Health
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Supporting your health, your rhythm, and your identity - whether or not you bleed.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  There are many reasons a woman might no longer menstruate - or never did. And yet, so much of women's healthcare is still cycle-dependent. At Women and the Way, we understand that health doesn't begin or end with your period. Our <a href="/perimenopause" className="text-amber-600 hover:text-amber-700 underline">perimenopause support</a> and <a href="/menopause" className="text-amber-600 hover:text-amber-700 underline">menopause care</a> extend to all women, regardless of their cycle status.
                </p>
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto text-center break-words bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  {isLoading ? 'Booking...' : 'Book Now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={heroImage} 
                  alt="Inclusive Chinese Medicine consultation for cycle-free women's health in Currimundi clinic" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why This Care Matters */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
                <Heart className="w-8 h-8 text-sage-600" />
                Why This Care Matters
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                Chinese medicine provides a holistic framework to support the whole person - not just reproductive organs. By working with Qi, Blood, Jing (essence), and Shen (spirit), we help regulate internal rhythms, ease discomfort, and guide you through times of transition.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                At Women and the Way, we honour all women - those who bleed monthly, those who no longer do, and those who never have. Whether you've experienced surgical menopause, hysterectomy, amenorrhea, early menopause, or are navigating gender transition, your health and wellbeing still deserve personalised, cyclical, and compassionate care.
              </p>
            </div>
          </div>
        </section>

        {/* Who This Is For */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                Who This Is For
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <CheckCircle className="w-8 h-8 text-sage-600 mb-4" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Post-Surgical Support</h3>
                  <p className="text-sage-700">Women who've had a hysterectomy or oophorectomy (removal of uterus and/or ovaries)</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <CheckCircle className="w-8 h-8 text-sage-600 mb-4" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Menopause Transition</h3>
                  <p className="text-sage-700">Those in surgical or early menopause. Learn more about our <a href="/menopause" className="text-amber-600 hover:text-amber-700 underline">menopause support</a>.</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <CheckCircle className="w-8 h-8 text-sage-600 mb-4" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Amenorrhea Support</h3>
                  <p className="text-sage-700">People experiencing hypothalamic amenorrhea, PCOS-related amenorrhea, or cycle loss due to stress, underweight, trauma, or medications</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <CheckCircle className="w-8 h-8 text-sage-600 mb-4" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Trans Women</h3>
                  <p className="text-sage-700">Trans women seeking support for hormonal balance, emotional wellbeing, or skin issues</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <CheckCircle className="w-8 h-8 text-sage-600 mb-4" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Recovery Support</h3>
                  <p className="text-sage-700">Women who've lost their cycle after chronic illness, eating disorders, or life transitions</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <CheckCircle className="w-8 h-8 text-sage-600 mb-4" />
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">Inclusive Care</h3>
                  <p className="text-sage-700">Anyone who identifies as a woman and seeks holistic health support, regardless of reproductive status</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Holistic Care for Your Unique Health Journey
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* In-Clinic Care */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Home className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">In-Clinic Care</h3>
                  </div>
                  <p className="text-sage-700 mb-4">
                    Personalised sessions at our Currimundi clinic, designed around your unique health needs.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🪡</span>
                      <p className="text-sage-700">Acupuncture to regulate hormones, support nervous system, and address specific symptoms</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Custom herbal formulas to support your individual constitution and health goals</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🔥</span>
                      <p className="text-sage-700">Moxa therapy for warming, tonifying, and supporting overall vitality</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌀</span>
                      <p className="text-sage-700">Lifestyle guidance, nutrition support, and stress management techniques</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Online Care */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Online Care</h3>
                  </div>
                  <p className="text-sage-700 mb-4">
                    Access our care from anywhere in Australia through telehealth consultations.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">💻</span>
                      <p className="text-sage-700">Comprehensive health consultations via video call</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">📦</span>
                      <p className="text-sage-700">Herbal medicine prescriptions mailed to your door</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">📚</span>
                      <p className="text-sage-700">Personalised lifestyle and nutrition guidance</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🔄</span>
                      <p className="text-sage-700">Ongoing support and treatment adjustments</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Book Your Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              Your health journey is unique. Let's create a care plan that honours your body, your story, and your goals.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
              onClick={handleBooking}
              disabled={isLoading}
            >
              {isLoading ? 'Booking...' : 'Start Your Health Journey'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
        
        {/* Footer removed - App.tsx handles global footer */}
        <ScrollDownArrow />
        <ReturnToTop />
      </div>
    </>
  );
};

export default CycleFreeHealthPage; 